import {computed, inject, Injectable, signal, Signal} from '@angular/core';
import {DtTableRegistry} from './dt-table-registry.service';
import {TableAction} from '../../table-actions/table-action.model';

export type TableActionsMobileMode = 'dropdown' | 'buttons';

/**
 * Service to manage global table actions dynamically across different pages.
 */
@Injectable({providedIn: 'root'})
export class DtActionsService {
  private readonly registry = inject(DtTableRegistry);

  /** The actions registered for the current view. */
  private readonly _actions = signal<TableAction[]>([]);

  /** Exposed signal of currently registered actions. */
  readonly actions: Signal<TableAction[]> = this._actions.asReadonly();

  /**
   * Modalità layout mobile:
   * - 'dropdown' (default): tutte le azioni collassate nel pulsante "Azioni"
   * - 'buttons': pulsanti inline visibili anche su mobile
   */
  private readonly _mobileMode = signal<TableActionsMobileMode>('dropdown');
  readonly mobileMode = this._mobileMode.asReadonly();

  setMobileMode(mode: TableActionsMobileMode): void {
    this._mobileMode.set(mode);
  }

  resetMobileMode(): void {
    this._mobileMode.set('dropdown');
  }

  /** Numero massimo di pulsanti visibili su desktop prima del dropdown "ALTRO" (default 3). */
  private readonly _maxVisible = signal<number>(3);
  readonly maxVisible = this._maxVisible.asReadonly();

  setMaxVisible(count: number): void {
    this._maxVisible.set(count);
  }

  resetMaxVisible(): void {
    this._maxVisible.set(3);
  }

  /**
   * Numero massimo di pulsanti visibili su viewport mobile prima di "ALTRO".
   * `null` (default) = nessun limite dedicato: su mobile vale `maxVisible`.
   * Impostarlo permette un layout mobile diverso dal desktop (es. 2 pulsanti
   * inline + "ALTRO"), senza toccare il desktop. Ha effetto solo con
   * `mobileMode = 'buttons'`.
   */
  private readonly _mobileMaxVisible = signal<number | null>(null);
  readonly mobileMaxVisible = this._mobileMaxVisible.asReadonly();

  setMobileMaxVisible(count: number): void {
    this._mobileMaxVisible.set(count);
  }

  resetMobileMaxVisible(): void {
    this._mobileMaxVisible.set(null);
  }

  /**
   * Sets the actions for the current view.
   * Typically called by a Page component on init.
   */
  setActions(actions: TableAction[]): void {
    this._actions.set(actions);
  }

  /**
   * Clears the current actions.
   * Typically called by a Page component on destroy.
   */
  clearActions(): void {
    this._actions.set([]);
  }

  /**
   * Returns a computed signal of actions that are dynamically enabled/disabled
   * based on the current selection of the specified table (or active table).
   */
  getEnhancedActions(tableId?: string): Signal<TableAction[]> {
    return computed(() => {
      const actions = this._actions();
      const tableRef = tableId
        ? this.registry.get(tableId)
        : this.registry.activeRef();

      if (!tableRef) {
        return actions.map(a => ({
          ...a,
          isDisabled: typeof a.isDisabled === 'function' ? true : a.isDisabled,
          badge: typeof a.badge === 'function' ? a.badge() : a.badge,
        }));
      }

      // Re-evaluate isDisabled, svgIcon, label e badge se sono funzioni
      return actions.map(action => {
        const isDisabled = typeof action.isDisabled === 'function'
          ? action.isDisabled()
          : action.isDisabled;

        const svgIcon = typeof action.svgIcon === 'function'
          ? action.svgIcon()
          : action.svgIcon;

        const label = typeof action.label === 'function'
          ? action.label()
          : action.label;

        const badge = typeof action.badge === 'function'
          ? action.badge()
          : action.badge;

        return {
          ...action,
          isDisabled,
          svgIcon,
          label,
          badge,
        };
      });
    });
  }
}
